<template>
  <ShortcutWrapper :nShortcutRequiredClaim="UserClaims.SustainabilityAdmin" @n-shortcut="createClicked">
    <v-container fluid>
      <v-row dense>
        <v-col cols="12">

          <Dialog
          :stateId="dialogId"
          :maxWidth="DialogWidth.SimpleWide"
          @click:outside="close"
          @dialog-closing="close"
          @close="close">
            <ConfirmDelete
            v-if="confirmDeleteDialog"
            @delete="performDelete"
            @cancel-delete="close"
            :maxWidth="DialogWidth.SimpleWide"
            :title="$t('mitigationGroup')">
              <template #custom-body>
                <v-alert
                v-if="speciesLayerEnabled()"
                type="warning"
                text
                outlined
                dense
                icon="mdi-alert-outline"
                class="mb-0 mg-delete-note">
                  <span class="text-caption">{{ $t('deleteMitigationGroupSpeciesLayerWarning') }}</span>
                </v-alert>
              </template>
            </ConfirmDelete>
            <MitigationGroupForm
            v-else
            :propMitigationGroup="mitigationGroup"
            @mitigation-group-mutated="refresh" />
          </Dialog>

          <v-row>
            <v-col>
              <v-row align="center" no-gutters>
                <v-icon color="primary" class="mr-2">mdi-wrench</v-icon>
                <span class="text-h6 font-weight-regular">{{ $t('customGroups') }}</span>
                <v-spacer/>
                <v-tooltip bottom color="grey" :disabled="endangeredSpeciesEnabled()">
                  <template #activator="{ on }">
                    <span v-on="on">
                      <Icon icon="mdi-plus"
                      :tooltipText="endangeredSpeciesEnabled() ? $t('addMitigationGroup') : undefined"
                      @icon-clicked="createClicked"
                      :small="false"
                      :disabled="!canCreate"
                      margin="mr-4"
                      elementId="mitigation-group-create"/>
                    </span>
                  </template>
                  <span class="subtitle-1 white--text">{{ $t('addGroupRequiresEndangeredSpeciesMode') }}</span>
                </v-tooltip>
              </v-row>
              <div class="text-caption d-flex align-center">
                {{ $t('mitigationGroupsSubtitle') }}
                <Icon
                v-if="allMitigationGroups.length > 0"
                icon="mdi-information-outline"
                iconColor="info"
                margin="ml-1"
                class="lh-0"
                :tooltipText="precedenceTooltip"/>
              </div>

              <v-skeleton-loader
              v-if="loading && allMitigationGroups.length === 0"
              type="table-row-divider@3"
              class="mt-3"/>

              <v-sheet
              v-else-if="allMitigationGroups.length === 0"
              outlined
              rounded
              class="pa-8 mt-3 text-center"
              style="border-style: dashed;">
                <v-icon large color="grey lighten-1">mdi-shape-outline</v-icon>
                <p class="text-subtitle-1 mb-1 mt-2">{{ $t('noGroupsDefined') }}</p>
                <p class="text-caption mx-auto mb-0" style="max-width: 34em;">{{ $t('mitigationGroupsInfo') }}</p>
              </v-sheet>

              <draggable
              v-else
              class="mt-3"
              :value="groupOrder"
              :disabled="!isSustainabilityAdmin"
              animation=400
              swapThreshold=".75"
              tag="v-row"
              :component-data="{ props: { dense: true } }"
              @input="persistOrder">
                <v-col cols="12" v-for="(group, position) in groupOrder" :key="group.mitigationGroupId">
                  <v-sheet
                  outlined
                  rounded
                  class="px-3 py-2 d-flex align-center"
                  :style="isSustainabilityAdmin ? 'cursor: move;' : undefined">
                    <v-icon v-if="isSustainabilityAdmin" small class="mr-1">mdi-drag-vertical</v-icon>
                    <span class="text-caption font-weight-bold mg-hex mg-rank">{{ position + 1 }}</span>
                    <span class="d-flex align-center mg-name">
                      <span class="mg-swatch mr-2" :style="{ backgroundColor: group.color }"/>
                      <span class="text-subtitle-2 text-truncate">{{ group.name }}</span>
                    </span>
                    <span class="text-caption text-truncate mg-description">{{ group.description }}</span>
                    <span class="d-flex align-center justify-end mg-flags">
                      <v-chip v-if="group.requiresHabitat" small outlined color="success">
                        <v-icon small left>mdi-check</v-icon>
                        {{ $t('requiresHabitat') }}
                      </v-chip>
                      <Icon
                      v-if="groupHasSteps(group)"
                      icon="mdi-clipboard-list-outline"
                      iconColor="info"
                      margin="ml-0 lh-0"
                      :tooltipText="$t('groupStepsDefined')"/>
                    </span>
                    <span class="d-flex align-center justify-end mg-actions">
                      <Icon
                      icon="mdi-pencil"
                      :tooltipText="$t('edit')"
                      @icon-clicked="mutate(group, 'edit')"
                      class="lh-0"
                      :disabled="!isSustainabilityAdmin"
                      elementId="mitigation-group-edit"/>
                      <Icon icon="mdi-delete-forever"
                      margin="ml-1 mr-0 lh-0"
                      :tooltipText="$t('delete')"
                      @icon-clicked="mutate(group, 'delete')"
                      :disabled="!isSustainabilityAdmin"
                      elementId="mitigation-group-delete"/>
                    </span>
                  </v-sheet>
                </v-col>
              </draggable>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
  </ShortcutWrapper>
</template>

<script>
import { cloneDeep } from 'lodash'
import draggable from 'vuedraggable'
import { mapActions, mapGetters } from 'vuex'
import { UserClaims } from '@/utils/Enumerations.js'
import { userAssignedClaim } from '@/utils/ClaimUtility'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { DialogWidth } from '@/utils/Dialog.js'
import { endangeredSpeciesEnabled, speciesLayerEnabled } from '@/utils/dueDiligence/mode.js'

export default {
  name: 'MitigationGroupSettings',

  components: {
    ConfirmDelete: () => import('@/components/helper/ConfirmDelete.vue'),
    ShortcutWrapper: () => import('@/components/core/ShortcutWrapper.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    MitigationGroupForm: () => import('@/components/settings-components/tract-settings/mitigation-group/MitigationGroupForm.vue'),
    draggable
  },

  data: () => ({
    confirmDeleteDialog: false,
    loading: false,
    mitigationGroup: undefined,
    dialogId: uniqueDialogId('mitigation-group-settings-dialog'),
    groupOrder: [],
    UserClaims,
    DialogWidth
  }),

  created () {
    this.refresh()
  },

  watch: {
    allMitigationGroups: {
      immediate: true,
      handler (groups) {
        this.groupOrder = groups.slice()
      }
    }
  },

  computed: {
    ...mapGetters('mitigation-group', ['allMitigationGroups']),

    isSustainabilityAdmin () {
      return userAssignedClaim(UserClaims.SustainabilityAdmin)
    },

    precedenceTooltip () {
      const lines = [this.$t('groupPrecedenceHint')]
      if (this.isSustainabilityAdmin) lines.push(this.$t('dragAndDropToReorder'))
      return lines.join('\n')
    },

    canCreate () {
      return this.isSustainabilityAdmin && endangeredSpeciesEnabled()
    }
  },

  methods: {
    ...mapActions('mitigation-group', ['fetchMitigationGroups', 'deleteMitigationGroup', 'reorderMitigationGroups']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),

    endangeredSpeciesEnabled,
    speciesLayerEnabled,

    refresh () {
      this.close()
      this.loading = true
      this.fetchMitigationGroups().finally(() => {
        this.loading = false
      })
    },

    createClicked () {
      if (!endangeredSpeciesEnabled()) {
        this.setSnackError(this.$t('addGroupRequiresEndangeredSpeciesMode'))
        return
      }
      this.mutate(null, 'create')
    },

    mutate (mitigationGroup, mutationType) {
      this.mitigationGroup = mitigationGroup ? cloneDeep(mitigationGroup) : undefined
      if (mutationType === 'delete') this.confirmDeleteDialog = true
      this.openOrUpdateDialog(this.dialogId)
    },

    close () {
      this.confirmDeleteDialog = false
      this.closeDialogsAtOrAbove(this.dialogId)
      this.mitigationGroup = undefined
    },

    groupHasSteps (item) {
      return !item.requiresHabitat && !!item.mitigationSteps?.trim()
    },

    async persistOrder (reordered) {
      if (!this.isSustainabilityAdmin) return
      const previousOrder = this.groupOrder
      this.groupOrder = reordered
      try {
        await this.reorderMitigationGroups(reordered.map(g => g.mitigationGroupId))
        this.setSnack(this.$t('groupOrderUpdated'))
      } catch (e) {
        this.groupOrder = previousOrder
        this.setSnackError(this.$t('groupOrderUpdateFailed'))
      }
    },

    async performDelete () {
      await this.deleteMitigationGroup(this.mitigationGroup.mitigationGroupId)
      this.setSnack(this.$t('mitigationGroupDeleted'))
      this.refresh()
    }
  }
}
</script>

<style scoped>
.mg-swatch {
  width: 1em;
  height: 1em;
  border-radius: 0.2em;
  display: inline-block;
  flex: 0 0 auto;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, .12) inset;
}

.mg-hex {
  font-variant-numeric: tabular-nums;
}

.mg-rank {
  flex: 0 0 1.5rem;
  text-align: right;
}

.mg-name {
  flex: 0 0 12rem;
  min-width: 0;
  padding-left: 0.75rem;
}

.mg-description {
  flex: 1 1 auto;
  min-width: 0;
  padding: 0 0.75rem;
}

.mg-flags {
  flex: 0 0 10.5rem;
}

.mg-actions {
  flex: 0 0 3.5rem;
}

.mg-delete-note ::v-deep .v-alert__icon {
  align-self: center;
}
</style>
